import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import {
  LogOut, LayoutDashboard, Inbox, FileText, Images, MessageSquare,
  Settings, Film, User, Award, Users as UsersIcon, Megaphone,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

import AdminOverview from "@/pages/admin/Overview";
import AdminLeads from "@/pages/admin/Leads";
import AdminContent from "@/pages/admin/Content";
import AdminGallery from "@/pages/admin/Gallery";
import AdminTestimonials from "@/pages/admin/Testimonials";
import AdminAwards from "@/pages/admin/Awards";
import AdminHeroMedia from "@/pages/admin/HeroMedia";
import AdminFounder from "@/pages/admin/FounderAdmin";
import AdminSettings from "@/pages/admin/Settings";
import AdminTeam from "@/pages/admin/Team";
import AdminLandings from "@/pages/admin/Landings";
import AdminChangePassword from "@/pages/admin/ChangePassword";

const TABS = [
  { to: "/admin", end: true, label: "Overview", Icon: LayoutDashboard, testid: "admin-tab-overview" },
  { to: "/admin/leads", label: "Leads", Icon: Inbox, testid: "admin-tab-leads" },
  { to: "/admin/landings", label: "Landings", Icon: Megaphone, testid: "admin-tab-landings" },
  { to: "/admin/hero", label: "Hero Media", Icon: Film, testid: "admin-tab-hero" },
  { to: "/admin/founder", label: "Founder", Icon: User, testid: "admin-tab-founder" },
  { to: "/admin/gallery", label: "Gallery", Icon: Images, testid: "admin-tab-gallery" },
  { to: "/admin/testimonials", label: "Reviews", Icon: MessageSquare, testid: "admin-tab-testimonials" },
  { to: "/admin/awards", label: "Awards", Icon: Award, testid: "admin-tab-awards" },
  { to: "/admin/content", label: "Content", Icon: FileText, testid: "admin-tab-content" },
  { to: "/admin/team", label: "Team", Icon: UsersIcon, testid: "admin-tab-team" },
  { to: "/admin/settings", label: "Settings", Icon: Settings, testid: "admin-tab-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ivory">
        <div className="font-display text-2xl text-charcoal/40 animate-pulse">Checking session&hellip;</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  // Force-change-password guard: any admin with the flag is locked to /admin/change-password
  // until they update it. We render the page bare (no nav) so it feels like a security wall.
  if (user.must_change_password && location.pathname !== "/admin/change-password") {
    return <Navigate to="/admin/change-password" replace />;
  }
  if (location.pathname === "/admin/change-password") {
    return <AdminChangePassword />;
  }

  return (
    <div className="min-h-screen bg-ivory flex flex-col md:flex-row" data-testid="admin-dashboard">
      <aside className="md:w-64 md:min-h-screen md:border-r border-beige bg-white md:py-8 md:px-6 px-4 py-4 flex md:flex-col flex-row items-center md:items-stretch justify-between md:justify-start gap-2 sticky top-0 z-30 md:h-screen border-b">
        <div className="hidden md:block mb-6">
          <div className="font-display text-xl text-charcoal leading-tight">Weddings by Mostash</div>
          <div className="uppercase tracking-[0.3em] text-[9px] text-charcoal/50 mt-1">Admin Studio</div>
        </div>
        <div className="md:hidden font-display text-base text-charcoal">Mostash · Admin</div>

        <nav className="hidden md:flex md:flex-col gap-1 flex-1 overflow-y-auto">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-sage/40 text-teal-600 border-l-2 border-teal-600 pl-3"
                    : "text-charcoal/70 hover:text-teal-600 hover:bg-sage/30"
                }`
              }
            >
              <t.Icon className="h-4 w-4" strokeWidth={1.5} />
              {t.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block mt-auto pt-4 border-t border-beige">
          <div className="text-[11px] text-charcoal/50 mb-2 truncate">{user.email}</div>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-btn"
            className="w-full inline-flex items-center justify-center gap-2 h-10 border border-charcoal/20 text-charcoal text-[11px] uppercase tracking-[0.22em] hover:bg-charcoal hover:text-ivory transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>

        <div className="md:hidden flex items-center gap-1 overflow-x-auto no-scrollbar -mr-2 pr-2">
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={`${t.testid}-mobile`}
              className={({ isActive }) =>
                `shrink-0 px-3 py-2 text-[11px] uppercase tracking-[0.18em] ${isActive ? "bg-teal-600 text-ivory" : "text-charcoal/70"}`
              }
            >
              {t.label}
            </NavLink>
          ))}
          <button type="button" onClick={logout} data-testid="admin-logout-btn-mobile" className="shrink-0 px-3 py-2 text-[11px] uppercase tracking-[0.18em] border border-charcoal/20 ml-1">
            <LogOut className="h-3 w-3" />
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-5 md:p-10 max-w-7xl">
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="leads" element={<AdminLeads />} />
          <Route path="hero" element={<AdminHeroMedia />} />
          <Route path="founder" element={<AdminFounder />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="gallery" element={<AdminGallery />} />
          <Route path="testimonials" element={<AdminTestimonials />} />
          <Route path="awards" element={<AdminAwards />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="landings" element={<AdminLandings />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </main>
    </div>
  );
}
